<template>
  <div
    v-if="!authUIFlags.isFetching && !accountUIFlags.isFetchingItem"
    id="app"
    class="app-wrapper h-full flex-grow-0 min-h-0 w-full"
    :class="{ 'app-rtl--wrapper': isRTLView }"
    :dir="isRTLView ? 'rtl' : 'ltr'"
  >
    <update-banner :latest-chatwoot-version="latestChatwootVersion" />
    <template v-if="currentAccountId">
      <payment-pending-banner />
      <upgrade-banner />
    </template>
    <transition name="fade" mode="out-in">
      <router-view />
    </transition>
    <add-account-modal
      :show="showAddAccountModal"
      :has-accounts="hasAccounts"
    />
    <woot-snackbar-box />
    <!--
      UNMOUNTED while a staging hang is investigated.
      ------------------------------------------------
      The dashboard was reported stuck on its loading state — the outer `v-if` above never
      satisfied — and these two were the only things this branch added to App.vue, so they come
      out first and go back in when the cause is known rather than assumed.

      The specific hazard that made them the first suspect: `settings-command` was registered as
      an ASYNC component. Vue 2 has no error boundary for one, so if its chunk fails to load —
      a deploy that did not publish it, a stale CDN, a request blocked in the browser — the
      failure surfaces during RENDER of this template and takes the whole tree with it, which
      looks exactly like an app that never finishes loading.

      Both components, their routes, their locale bundles and their tests are all still on the
      branch and unchanged. This is two lines of mounting, not a revert of the work.
    -->
    <!-- <network-notification /> -->
  </div>
  <loading-state v-else />
</template>

<script>
import { mapGetters } from 'vuex';
import AddAccountModal from '../dashboard/components/layout/sidebarComponents/AddAccountModal.vue';
import LoadingState from './components/widgets/LoadingState.vue';
// import NetworkNotification from './components/NetworkNotification.vue';
import UpdateBanner from './components/app/UpdateBanner.vue';
import UpgradeBanner from './components/app/UpgradeBanner.vue';
import PaymentPendingBanner from './components/app/PaymentPendingBanner.vue';
import vueActionCable from './helper/actionCable';
import WootSnackbarBox from './components/SnackbarContainer.vue';
import rtlMixin from 'shared/mixins/rtlMixin';
import { setColorTheme } from './helper/themeHelper';
import {
  registerSubscription,
  verifyServiceWorkerExistence,
} from './helper/pushHelper';

export default {
  name: 'App',

  components: {
    AddAccountModal,
    LoadingState,
    // NetworkNotification,
    UpdateBanner,
    PaymentPendingBanner,
    WootSnackbarBox,
    UpgradeBanner,
  },

  mixins: [rtlMixin],

  data() {
    return {
      showAddAccountModal: false,
      latestChatwootVersion: null,
    };
  },

  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      currentUser: 'getCurrentUser',
      globalConfig: 'globalConfig/get',
      authUIFlags: 'getAuthUIFlags',
      accountUIFlags: 'accounts/getUIFlags',
      currentAccountId: 'getCurrentAccountId',
    }),
    hasAccounts() {
      const { accounts = [] } = this.currentUser || {};
      return accounts.length > 0;
    },
  },

  // ONE watch block. There were two, and a duplicate key in an object literal is not an error —
  // the second silently replaces the first. The RTL watcher below was added as its own block, which
  // deleted the two watchers above it, and nothing reported anything.
  //
  // What that cost: `currentAccountId` is what calls `initializeAccount`, so `accounts/get` was
  // never dispatched. The accounts store stayed empty for the whole session, which is why account
  // settings rendered a blank form over a real account, and why choosing a site language did
  // nothing — `setLocale` from the account lives in that same dead method. The add-account modal
  // for a user with no accounts went with it.
  watch: {
    currentUser() {
      if (!this.hasAccounts) {
        this.showAddAccountModal = true;
      }
    },
    // The one that fetches the account record. It was silently lost for a while: a SECOND
    // `watch:` key was added to this same object literal, and the later one simply replaces the
    // earlier — no error, no warning, the whole block just stops existing.
    //
    // Nothing else dispatches `accounts/get`, so the store held no account at all. The sidebar
    // still showed the right name because it reads the user payload, which made it look as
    // though the account had loaded — while Account settings rendered an empty form and an empty
    // Account ID, and Account health reported that none of the account's settings could be read.
    currentAccountId() {
      if (this.currentAccountId) {
        this.initializeAccount();
      }
    },
    // On the documentElement, not only on `#app`.
    //
    // `#app` is inside a `v-if` that is false until auth and the account have loaded, so
    // anything rendered outside it — a tooltip in the default container, most of all — had no
    // ancestor carrying `dir` and laid itself out left-to-right in an Arabic dashboard. The
    // documentElement is always there, so the direction is correct from the first paint and for
    // everything appended to the body.
    isRTLView: {
      immediate: true,
      handler(isRTL) {
        document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
      },
    },
  },
  mounted() {
    this.initializeColorTheme();
    this.listenToThemeChanges();
    this.setLocale(window.chatwootConfig.selectedLocale);
  },
  methods: {
    initializeColorTheme() {
      setColorTheme(window.matchMedia('(prefers-color-scheme: dark)').matches);
    },
    listenToThemeChanges() {
      const mql = window.matchMedia('(prefers-color-scheme: dark)');
      mql.onchange = e => setColorTheme(e.matches);
    },
    setLocale(locale) {
      this.$root.$i18n.locale = locale;
    },
    async initializeAccount() {
      await this.$store.dispatch('accounts/get');
      this.$store.dispatch('setActiveAccount', {
        accountId: this.currentAccountId,
      });
      const { locale, latest_chatwoot_version: latestChatwootVersion } =
        this.getAccount(this.currentAccountId);
      const { pubsub_token: pubsubToken } = this.currentUser || {};
      this.setLocale(locale);
      this.updateRTLDirectionView(locale);
      this.latestChatwootVersion = latestChatwootVersion;
      vueActionCable.init(pubsubToken);

      verifyServiceWorkerExistence(registration =>
        registration.pushManager.getSubscription().then(subscription => {
          if (subscription) {
            registerSubscription();
          }
        })
      );
    },
  },
};
</script>

<style lang="scss">
@import './assets/scss/app';
</style>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
